// Contact Nexus — global support widget. AI-triage → ticket creation.
import { useState } from "react";
import { Headphones, X, Send, Bot } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "I can't withdraw / payouts blocked",
  "Sticker animations / sounds not playing",
  "PayPal checkout failed",
  "Stream won't go live",
  "I'm being harassed in chat",
  "Question about Studio Tier",
];

// variant="fab" (default) -- fixed floating button, used site-wide.
// variant="inline" -- plain inline button, used inside the Studio Dashboard
// header instead, so it never floats over a live stream/chat.
export default function ContactNexus({ variant = "fab" }) {
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState([
    { who: "ai", text: "Hi — I'm the NEXUS support bot. Tell me what's wrong and I'll either fix it or escalate to the owner." }
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    const message = text || draft;
    if (!message.trim()) return;
    setConversation((c) => [...c, { who: "me", text: message }]);
    setDraft(""); setBusy(true);
    try {
      const { data } = await api.post("/eden/ask", { prompt: `[NEXUS SUPPORT] User says: "${message}". You are the NEXUS support bot. If you can answer with a clear fix, do so. If it's a billing/account/abuse issue, end with "I'll escalate this to the owner for you." in your reply.`, context: "support" });
      setConversation((c) => [...c, { who: "ai", text: data.reply }]);
      // If AI says escalate OR contains keywords, create ticket
      if (/escalate|owner|refund|billing|payout|fraud|hack|stolen|harass/i.test(data.reply + " " + message)) {
        const summary = message.slice(0, 60);
        try {
          await api.post("/support/tickets", { subject: summary, body: message, category: "general" });
          setConversation((c) => [...c, { who: "ai", text: "✓ Ticket sent to owner inbox. Reply will appear in your in-app inbox." }]);
        } catch {}
      }
    } catch {
      setConversation((c) => [...c, { who: "ai", text: "I hit an issue connecting to AI. I'll file your message as a ticket anyway." }]);
      try { await api.post("/support/tickets", { subject: message.slice(0, 60), body: message, category: "general" }); } catch {}
    }
    setBusy(false);
  };

  return (
    <>
      {variant === "inline" ? (
        <button onClick={() => setOpen(true)} className="px-3 py-1.5 text-[10px] tracking-[0.3em] font-display uppercase bg-nexus-cyan/15 text-nexus-cyan border border-nexus-cyan/40 hover:bg-nexus-cyan/25 transition-colors flex items-center gap-1.5" data-testid="contact-nexus-inline" title="Contact NEXUS">
          <Headphones className="w-3.5 h-3.5" /> Support
        </button>
      ) : (
        <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-40 bg-nexus-cyan/20 hover:bg-nexus-cyan/40 text-nexus-cyan border border-nexus-cyan/40 rounded-full p-3 backdrop-blur-md shadow-lg shadow-nexus-cyan/20" data-testid="contact-nexus-fab" title="Contact NEXUS">
          <Headphones className="w-5 h-5" />
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-end sm:justify-center bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="glass-strong w-full sm:max-w-md h-[70vh] sm:h-[600px] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="contact-nexus-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-nexus-cyan" /><div className="font-display text-sm tracking-wider">CONTACT NEXUS</div></div>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-white/40" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {conversation.map((m, i) => (
                <div key={i} className={`max-w-[85%] p-3 text-sm ${m.who === "me" ? "ml-auto bg-nexus-cyan/15 text-white" : "bg-nexus-surface text-white/80 border-l-2 border-nexus-cyan"}`}>{m.text}</div>
              ))}
              {busy && <div className="text-[10px] text-white/40 tracking-[0.3em] uppercase">AI thinking…</div>}
              {conversation.length === 1 && (
                <div className="space-y-1 pt-3">
                  <div className="label-eyebrow">// quick topics</div>
                  {QUICK_PROMPTS.map((p) => (
                    <button key={p} onClick={() => send(p)} className="block w-full text-left text-xs p-2 border border-white/10 hover:border-nexus-cyan text-white/70">{p}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your question…" className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="contact-input" />
              <button onClick={() => send()} className="btn-nexus" data-testid="contact-send"><Send className="w-3 h-3" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
